import { useQuery } from '@tanstack/react-query';
import get from 'lodash-es/get';
import React, { createContext, useContext, useMemo } from 'react';

import { API_CONSTANTS } from '../constants/apiUrls';
import constantsDefaults from '../defaults/constantsDefaults';
import { getRequestJSON } from '../util/services';
import { useAuth } from './useAuth';
import { useConfig } from './useConfig';

type ConstantsContextType = {
  constants: any;
  isLoading: boolean;
  isSuccess: boolean;
  getConstant: (key: string) => any;
};

const ConstantsContext = createContext<ConstantsContextType | undefined>(undefined);

type ConstantsProviderProps = {
  children: React.ReactNode;
};

export function ConstantsProvider({ children }: ConstantsProviderProps) {
  const { apiHost } = useConfig();
  const auth = useAuth();
  const user = get(auth, 'user');

  const {
    data,
    isLoading,
    isFetching,
    isRefetching,
    isSuccess,
  } = useQuery({
    queryKey: ['constants'],
    queryFn: () => user && getRequestJSON(apiHost, API_CONSTANTS),
  });

  // Todo: Remove this once the other block is working
  const dynamicConstants = null;
  // const { loading, data: dynamicConstants } = useGQuery(GET_CONSTANTS);

  const getConstant = (key: string) =>
    get(dynamicConstants, 'constants', []).find((item: any) => item.key === key);

  const value = useMemo(
    () => ({
      constants: data || constantsDefaults,
      getConstant: dynamicConstants ? getConstant : null,
      isLoading: isLoading || isFetching || isRefetching,
      isSuccess,
    }),
    [data, isLoading, isFetching, isRefetching, isSuccess, dynamicConstants]
  );

  return <ConstantsContext.Provider value={value}>{children}</ConstantsContext.Provider>;
}

export const useConstants = (): ConstantsContextType => useContext(ConstantsContext);
